import React, { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useTheme } from '../ThemeContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, toggle, t } = useLang();
  const theme = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goAnchor = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const items = [
    { type: 'route', to: '/nos-patisseries', label: t.nav.pastries },
    { type: 'route', to: '/nos-pates', label: t.nav.pates },
    { type: 'route', to: '/notre-histoire', label: t.nav.history },
    { type: 'anchor', id: 'contact', label: t.nav.contact }
  ];

  const renderItems = (extra = '') => items.map((item, i) => {
              const active = item.type === 'route' && location.pathname === item.to;
              const base =
                `${extra} text-[10px] lg:text-[11px] tracking-[0.06em] md:tracking-[0.18em] lg:tracking-[0.2em] uppercase hover:text-[#d4a94c] transition-colors duration-300 whitespace-nowrap`;
              if (item.type === 'route') {
                return (
                  <Link
                    key={i}
                    to={item.to}
                    className={`${base} ${active ? 'text-[#d4a94c]' : 'text-[#f6ecd9]'}`}
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <button key={i} onClick={() => goAnchor(item.id)} className={`${base} text-[#f6ecd9]`}>
                  {item.label}
                </button>
              );
            });

  return (
    <div
      className="fixed left-0 right-0 z-50 flex justify-center px-4"
      style={{ top: 'calc(var(--banner-h, 0px) + 16px)' }}
    >
      <nav
        className="w-full max-w-6xl rounded-3xl md:rounded-full border transition-all duration-500 backdrop-blur-md shadow-xl"
        style={{
          background: scrolled ? theme.primary95 : theme.primary85,
          borderColor: 'rgba(212, 169, 76, 0.3)'
        }}
      >
        <div className="flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_murat-sweet/artifacts/c7p1pt64_LOGO%20MURAT.jpg"
              alt="Murat"
              className="h-10 w-auto object-contain"
              style={{ filter: 'invert(1) hue-rotate(180deg)', mixBlendMode: 'lighten' }}
            />
            <span className="hidden sm:inline-flex flex-col">
              <span className="text-[9px] tracking-[0.3em] text-[#d4a94c] uppercase leading-none">
                {t.nav.since}
              </span>
              <span className="text-[8px] tracking-[0.3em] text-[#f6ecd9]/60 uppercase mt-1 leading-none">
                Feuilletées & Pâtisseries
              </span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {renderItems()}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#f6ecd9]/20 hover:border-[#d4a94c] text-[#f6ecd9] hover:text-[#d4a94c] text-[10px] tracking-[0.2em] uppercase font-medium transition-all duration-300"
              aria-label="Change language"
            >
              <Globe className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className={lang === 'fr' ? 'text-[#d4a94c]' : ''}>FR</span>
              <span className="text-[#f6ecd9]/40">/</span>
              <span className={lang === 'en' ? 'text-[#d4a94c]' : ''}>EN</span>
            </button>
            <button
              onClick={() => goAnchor('contact')}
              className="hidden xl:inline-block px-5 py-2.5 rounded-full border border-[#d4a94c] text-[#d4a94c] hover:text-[#0d1b36] text-[11px] tracking-[0.2em] uppercase font-medium transition-all duration-300"
              style={{ background: 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#d4a94c')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
        <div
          data-testid="mobile-nav-tabs"
          className="md:hidden flex items-center justify-between gap-2 px-4 pb-3 pt-2 border-t border-[#d4a94c]/20 overflow-x-auto"
        >
          {renderItems('py-1')}
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
